import {withDisplayErrorToUser} from "../error/error";
import {IRouteChanged, subscribeToRouteChanged} from "../eventBus/route/routeEventBus";
import {appRoutePaths} from "../../config/config";
import {store} from "../../redux/store";
import playgroundApi from "../playgroundApi";
import {
  createBootCampPageViewModel,
  createBootcampUrl,
  createInitialBootCampPageViewModel,
  getBootCampCourseIdFromLocationPath,
  getCourseIdFromDifficultyOption
} from "../../factories/bootCampPageFactory";
import {DifficultyOption} from "../../models/view/common/DifficultyOption";
import IBootCampPageModel from "../../models/view/bootCampPage/IBootCampPageModel";
import {createEventBusAndHook} from "../eventBus/common";
import ICard from "../../models/view/common/ICard";
import settingsService from "../settings/settingsService";
import MyLibraryResponse from "../../client/models/myLibrary/MyLibraryResponse";
import {bootcampLastCourseIdRepository} from "../../repositories/bootcamp/bootcamp-last-course-id";
import {isDefined} from "../../util/is-type";
import {navigate} from "../navigation/navigation";

const bootCampCourseIds = {
  35: "rookie",
  36: "intermediate",
  37: "advanced",
};

const initialBootCampPageViewModel = createInitialBootCampPageViewModel();

export const {subscribe: subscribeToBootCampPageViewModelChanged, emitMessage: bootCampPageViewModelChanged, use: useBootCampPageViewModel} = createEventBusAndHook<IBootCampPageModel>(initialBootCampPageViewModel);

class Bootcamp{
  //array of unsubscribe functions to call when this instance is destroyed.
  unsubscribeFuncs: (() => void)[] = [];
  bootCampPageViewModel = initialBootCampPageViewModel; //hold ref so we can display modal
  constructor(){
    this.unsubscribeFuncs = [
      subscribeToRouteChanged(this.handleRouteChange.bind(this)),
    ];
  }
  destroy(){
    this.unsubscribeFuncs.forEach(u => u());
  }

  async handleRouteChange({routePath, locationPath, userHasValidToken}: IRouteChanged){
    if(appRoutePaths.bootCampPage !== routePath || !userHasValidToken){
      return;
    }

    let {courseId} = getBootCampCourseIdFromLocationPath(locationPath);
    const isCourseIdValid = isDefined(courseId) && bootCampCourseIds[courseId as keyof typeof bootCampCourseIds] !== undefined;

    if(!isCourseIdValid) {
      courseId = undefined;
    }

    if(!courseId) {
      const courseIdFromStorage = bootcampLastCourseIdRepository.get();
      if(courseIdFromStorage) {
        courseId = parseInt(courseIdFromStorage);
      } else {
        return navigateToDifficultyOption("rookie");
      }
    }
    bootcampLastCourseIdRepository.set(courseId);
    await this.loadBootCampViewModel(courseId);
  }

  /**
   *
   * @param courseId - will be undefined when it's not part of the bootcamp path. In that case we'll want to use the course id from featured lesson.
   */
  async loadBootCampViewModel(courseId: number | undefined) {
    return withDisplayErrorToUser(async () => {
      this.changeIsLoading(true);
      const myLibraryResponse = await playgroundApi.myLibrary();
      const hasAccessToBootcamp = doesUserHaveAccessToBootcamp(myLibraryResponse);

      const featuredLessonCourseId = myLibraryResponse.featuredBootcampLesson.courseId;
      if(courseId === undefined){
        courseId = featuredLessonCourseId;
      }
      //if the user doesn't have access, don't show the modal on the first time, as the backend isn't giving us the data we need.
      const hasBootcampModalBeenDisplayed = hasAccessToBootcamp ? settingsService.getHasBootcampModalBeenDisplayed() : true;
      settingsService.changeHasBootcampModalBeenDisplayed(true);

      //@ts-ignore
      const difficultyNavigationOption = hasAccessToBootcamp ? bootCampCourseIds[courseId] : "none";
      let bootCampCourse;
      if(hasAccessToBootcamp){ //don't make the call if they don't have access.
        bootCampCourse = await playgroundApi.myCourse({courseId});
      }

      this.bootCampPageViewModel = createBootCampPageViewModel(
        bootCampCourse,
        courseId,
        difficultyNavigationOption,
        myLibraryResponse,
        hasBootcampModalBeenDisplayed,
        hasAccessToBootcamp
      );
      bootCampPageViewModelChanged(this.bootCampPageViewModel);

    });
  }

  displayInfoModal(isVisible: boolean){
    this.bootCampPageViewModel = {...this.bootCampPageViewModel, infoModal: {...this.bootCampPageViewModel.infoModal, isVisible, isFirstTimeViewed: false}};
    bootCampPageViewModelChanged(this.bootCampPageViewModel);
  }

  changeIsLoading(isLoading: boolean){
    this.bootCampPageViewModel = {...this.bootCampPageViewModel, isLoading};
    bootCampPageViewModelChanged(this.bootCampPageViewModel);
  }
}

function doesUserHaveAccessToBootcamp(myLibraryResponse: MyLibraryResponse){
  const bootcampCourse = myLibraryResponse.courseSections.find(c => c.name === "Bootcamp");
  return bootcampCourse !== undefined;
}

const bootcampService = new Bootcamp();
export default bootcampService;

export function displayInfoModal(isVisible: boolean){
  bootcampService.displayInfoModal(isVisible);
}

export function cardClicked(card: ICard){
  navigate(card.relativeUrl);
}

function navigateToCourseId(courseId: number){
  const url = createBootcampUrl(courseId);
  navigate(url);
  return;
}

function navigateToDifficultyOption(difficultyOption: DifficultyOption){
  const courseId = getCourseIdFromDifficultyOption(difficultyOption);
  navigateToCourseId(courseId);
}